import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { ProtectedImage } from "../ProtectedImage";
import { ImageSkeleton } from "../loading/ImageSkeleton";

import { ReleaseContentCardProps } from "../../types";
import { BookmarkIcon } from "../icons/BookmarkIcon";
import {
  ContentCardBackdrop,
  ExtendedContentCardBackdrop,
} from "./components/ContentCardBackdrop";
import { ContentCardFooter } from "./components/ContentCardFooter";
import { FreeRibbon } from "./components/FreeRibbon";
import { GridCardWrapper } from "./components/GridCardWrapper";
import { useGridCardDimensions } from "./hooks/useGridCardDimensions";
import { ReleasePriceLabel } from "./ReleasePriceLabel";
import { getReleaseTypeName } from "../../utils/getReleaseTypeName";
import { LockedContentBadge } from "./components/LockedContentBadge";
import { SaleBadge } from "./components/SaleBadge";
import { PurchasedContentBadge } from "./components/PurchasedContentBadge";
import { GoldRewardBadge } from "./components/GoldRewardBadge";

export function ReleaseGridCard({
  content,
  layout: { display, style },
  isLocked,
  wasPurchased,
  hideBackdrop,
  fullWidth,
  showPurchasedBadge,
}: ReleaseContentCardProps) {
  const layout_size = style.size;

  const { height, width } = useGridCardDimensions(
    "releases",
    style.mode,
    layout_size,
  );

  switch (layout_size) {
    case "s":
      return (
        <WideReleaseGridCard
          content={content}
          layout={{
            display,
            style,
          }}
          width={width}
          height={height}
        />
      );
    case "m":
    case "l":
    case "v2":
      return (
        <GridCardWrapper
          layout={{ display, style }}
          fullHeight={hideBackdrop}
          fullWidth={fullWidth || hideBackdrop}
          hideBackdrop={hideBackdrop}
        >
          <TallReleaseGridCard
            content={content}
            layout={{
              display,
              style,
            }}
            isLocked={isLocked}
            wasPurchased={wasPurchased}
            showPurchasedBadge={showPurchasedBadge}
            width={width}
            height={height}
            isSmall={layout_size === "m"}
            isLarge={layout_size === "l"}
            isV2={layout_size === "v2"}
            hideBackdrop={hideBackdrop}
          />
        </GridCardWrapper>
      );
    default:
      console.warn("Unsupported card size", layout_size);
      return null;
  }
}

function WideReleaseGridCard({
  content,
  layout: { display },
  height,
  width,
}: ReleaseContentCardProps & {
  height?: number;
  width?: number;
}) {
  const { include_name, include_owner_name, include_title } = display;

  return (
    <Flex
      height={`${height}px`}
      width={`${width}px`}
      direction="row"
      position="relative"
      alignItems="center"
      justifyContent="start"
    >
      <ContentCardBackdrop
        position="absolute"
        bottom={0}
        width="calc(100% - 8px)"
        right={0}
      />
      <Box position="relative">
        <ProtectedImage
          src={content.thumbnail_url}
          width="50px"
          minWidth="50px"
          borderRadius={10}
          alt={`Cover image for ${content.localized_comic_name}`}
          fallback={
            <ImageSkeleton width="50px" minWidth="50px" borderRadius={10} />
          }
        />
        {content.is_free ? (
          <FreeRibbon
            color="success.500"
            position="absolute"
            top={0}
            left="-1px"
          />
        ) : null}
      </Box>
      <Flex
        direction="column"
        position="relative"
        pl={3}
        width="calc(100% - 58px)"
      >
        {include_name ? (
          <Text variant="cardSmallHeader" noOfLines={1}>
            {content.localized_comic_name}
          </Text>
        ) : null}
        {include_title ? (
          <Text variant="cardSmallHeader" noOfLines={1}>
            {content.title}
          </Text>
        ) : null}
        {include_owner_name ? (
          <Text color="neutral.200" variant="cardSubtitle">
            {content.artist?.roman_name}
          </Text>
        ) : null}
      </Flex>
    </Flex>
  );
}

function TallReleaseGridCard({
  content,
  layout: { display },
  onToggleFavorite,
  isSmall,
  isLarge,
  isV2,
  isLocked,
  hideBackdrop,
  wasPurchased,
  showPurchasedBadge,
}: ReleaseContentCardProps & {
  height?: number;
  width?: number;
  isSmall?: boolean;
  isLarge?: boolean;
  isV2?: boolean;
}) {
  const { include_name, include_favorite, include_title, include_owner_name } =
    display;

  const comic = content.comic;

  return (
    <>
      {!hideBackdrop && !isV2 && (
        <ExtendedContentCardBackdrop
          position="absolute"
          bottom="0px"
          h="calc(100% - 63px)"
          topBox={{
            height: "63px",
          }}
          bottomBox={{
            height: "calc(100% - 63px)",
          }}
        />
      )}
      <Box className="card-img-wrapper" aspectRatio=".646">
        <ProtectedImage
          src={content.thumbnail_url}
          borderRadius={isV2 ? "8px" : 10}
          border={isV2 ? "1px solid" : undefined}
          borderColor={isV2 ? "transparent.white.20" : undefined}
          width="100%"
          aspectRatio=".646"
          objectFit="cover"
          alt={`Cover image for ${content.localized_comic_name}`}
          fallback={
            <ImageSkeleton
              width="100%"
              aspectRatio=".646"
              borderRadius={isV2 ? "8px" : 10}
            />
          }
        />
        {(content.is_free ?? false) ? (
          <FreeRibbon
            color="success.500"
            position="absolute"
            top={0}
            left="-1px"
            width="3px"
            height="8px"
            fontSize="sm"
          />
        ) : null}
        {isLarge && <SaleBadge release={content} />}
        {isLarge && !!isLocked && (
          <LockedContentBadge
            position="absolute"
            bottom={0}
            left="-1px"
            boxSize="22px"
          />
        )}
        {(isLarge || isV2) && !!wasPurchased && content.release_user_purchase?.context_entity_type_id === 46 && (
          <GoldRewardBadge
            position="absolute"
            bottom={0}
            left="-1px"
            fontSize="10px"
          />
        )}
        {isLarge && !!wasPurchased && showPurchasedBadge && content.release_user_purchase?.context_entity_type_id !== 46 && (
          <PurchasedContentBadge
            position="absolute"
            bottom={0}
            left="-1px"
            text="Purchased"
            fontSize="10px"
          />
        )}
      </Box>
      {!isSmall ? (
        <ContentCardFooter
          total_favorites={comic?.total_favorites}
          relevance_match={comic?.relevance_match}
          update_time={content.update_time}
          total_published_releases={comic?.total_published_releases}
          page_count={content.page_count}
          {...display}
          include_match={false}
          layoutMode="list"
        />
      ) : null}
      <Flex direction="column" position="relative" width="100%">
        {isLarge && (
          <Text variant="cardSubtitle" color="charcoal.200" noOfLines={1}>
            {getReleaseTypeName(content)} #{content.chapter}
          </Text>
        )}

        {include_name ? (
          <Text variant={isSmall ? "cardSubtitle" : "cardHeader"} noOfLines={1}>
            {content.localized_comic_name}
          </Text>
        ) : null}
        {include_title ? (
          <Text variant={isSmall ? "cardSubtitle" : "cardHeader"} noOfLines={1}>
            {content.title}
          </Text>
        ) : null}
        {include_owner_name ? (
          <Text color="reader.neutral.200" variant="cardSubtitle">
            {content.artist?.roman_name}
          </Text>
        ) : null}

        {(isLarge || isV2) && (
          <Box pt="4px" {...(isV2 && { h: "20px", overflow: "hidden" })}>
            <ReleasePriceLabel
              release={content}
              fontSize={isV2 ? "13px" : "14px"}
              compact
              showPurchasedLabel={!showPurchasedBadge}
              displayAsAColumn={!isV2}
            />
          </Box>
        )}
      </Flex>
      <Flex
        direction="column"
        gap={2}
        alignItems="start"
        width="100%"
        position="relative"
      >
        {include_favorite && comic ? (
          <Button
            variant="ghost"
            w="full"
            h={7}
            bgGradient="linear(to-l, alt.blue_gradient_top, alt.blue_gradient_bottom)"
            boxShadow="0px 8px 12px #12151a4d"
            borderRadius={10}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggleFavorite?.(comic.slug, comic.is_favorite);
            }}
          >
            <Flex direction="row" gap={2} alignItems="center">
              <BookmarkIcon color="neutral.white" />
              <Text fontWeight="700" fontSize="10px" lineHeight="11px">
                ADD
              </Text>
            </Flex>
          </Button>
        ) : null}
      </Flex>
    </>
  );
}
